import React, { useEffect } from 'react';
import teamImage from '../assests/images/About2.png'; // Replace with your image path
import { motion } from 'framer-motion';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { FiBriefcase, FiShield } from 'react-icons/fi';
import { MdPublic, MdOutlineFolderSpecial } from 'react-icons/md';
import { Helmet } from 'react-helmet';

const About = () => {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  return (
    <>
      <Helmet>
        <title>About Us | Kavipriya Associates - Trusted Tax & Compliance Experts in Coimbatore</title>
        <meta
          name="description"
          content="Learn about Kavipriya Associates - your trusted partner for tax, GST, DGFT, customs, and compliance services. 7+ years of expertise serving Startups, MSMEs & Corporates across India."
        />
        <meta
          name="keywords"
          content="About Kavipriya Associates, Tax Consultant Coimbatore, GST Expert, DGFT Services, Customs Clearance, Financial Services, Compliance Consultant, MSME Support, Startup Services"
        />
        <meta name="author" content="Kavipriya Associates" />
        <link rel="canonical" href="https://kavipriyaassociates.com/about" />

        {/* Open Graph / Facebook */}
        <meta property="og:title" content="About Kavipriya Associates - Trusted Tax & Compliance Experts" />
        <meta
          property="og:description"
          content="7+ years of expertise in tax, GST, DGFT, customs, and compliance services. Serving Startups, MSMEs & Corporates across India from Coimbatore."
        />
        <meta property="og:image" content="https://kavipriyaassociates.com/Logo.png" />
        <meta property="og:url" content="https://kavipriyaassociates.com/about" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:title" content="About Kavipriya Associates - Your Trusted Finance Partner" />
        <meta
          name="twitter:description"
          content="Expert tax, GST, DGFT, and compliance services with 7+ years of experience. Based in Coimbatore, serving PAN India clients."
        />
        <meta name="twitter:image" content="https://kavipriyaassociates.com/Logo.png" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Kavipriya Associates",
      "url": "https://kavipriyaassociates.com",
      "logo": "https://kavipriyaassociates.com/logo.png",
      "description": "Trusted partner in tax, finance & compliance services for Startups, MSMEs & Corporates across India",
      "foundingDate": "2017",
      "numberOfEmployees": "10-50",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Coimbatore",
        "addressRegion": "Tamil Nadu",
        "addressCountry": "India"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+91-9876543210",
        "contactType": "customer service",
        "areaServed": "IN",
        "availableLanguage": "English"
      },
      "sameAs": [
        "https://www.facebook.com/YOURPAGE",
        "https://www.linkedin.com/company/YOURPAGE"
      ]
    }
    `}
        </script>
      </Helmet>

      <section className="relative bg-gradient-to-br from-white via-blue-50 to-purple-50 overflow-hidden py-16 px-4 sm:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 items-center gap-12">

          {/* Left Section */}
          <div data-aos="fade-right">
            <p className="text-sm text-black font-semibold uppercase mb-2">Welcome to Kavipriya Associates</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Trusted Partner in <span className="text-purple-800">Tax,</span> <span className="text-purple-800">Finance</span> & <span className="text-purple-800">Compliance</span>
            </h1>
            <p className="text-gray-700 text-lg mb-4">
              At <strong>Kavipriya Associates</strong>, we are committed to being your reliable partner in navigating the complex world of <span className="text-blue-600 font-semibold">taxation</span>, <span className="text-blue-600 font-semibold">finance</span>, and <span className="text-blue-600 font-semibold">government compliance</span>. Based in Coimbatore, Tamil Nadu, our firm offers a comprehensive suite of services tailored for Startups, MSMEs, and Corporates across India.
            </p>
            <p className="text-gray-700 text-lg mb-4">
              Our areas of expertise include <span className="text-purple-700 font-medium">GST filings</span>, <span className="text-purple-700 font-medium">Income Tax returns</span>, Import/Export licensing through DGFT, Customs clearance, Insurance advisory, and full statutory compliance support including PF, ESI, and Factory licensing.
            </p>
            <p className="text-gray-700 text-lg mb-6">
              With a deep understanding of the Indian financial ecosystem and regulatory landscape, we help clients unlock growth opportunities and ensure legal compliance with confidence.
            </p>
          </div>

          {/* Right Section */}
          <div className="relative w-full h-full flex justify-center items-center" data-aos="fade-left">
            <div className="relative w-[300px] sm:w-[400px] md:w-[500px] lg:w-[550px]">
              <img
                src={teamImage}
                alt="Team working"
                className="rounded-3xl shadow-xl w-full clip-custom-shape object-cover"
              />
              <div className="absolute top-0 left-0 w-full h-full -z-10">
                <div className="w-full h-full bg-gradient-to-br from-pink-100 via-blue-100 to-white rounded-full blur-3xl opacity-70"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Service Highlights */}
        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-aos="fade-up">
          {[
            {
              icon: <FiBriefcase className="text-4xl text-purple-700 mb-3" />,
              title: '7+ Years Experience',
              desc: 'Expertise across finance, tax, and compliance domains.',
            },
            {
              icon: <MdPublic className="text-4xl text-purple-700 mb-3" />,
              title: 'PAN India Clients',
              desc: 'Serving startups, MSMEs, and corporates across India.',
            },
            {
              icon: <MdOutlineFolderSpecial className="text-4xl text-purple-700 mb-3" />,
              title: 'DGFT & Customs Experts',
              desc: 'Specialized in import/export licensing and clearances.',
            },
            {
              icon: <FiShield className="text-4xl text-purple-700 mb-3" />,
              title: 'Secure & Compliant',
              desc: 'Fully compliant with Indian statutory and financial norms.',
            },
          ].map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-md p-6 text-center hover:shadow-xl transition-shadow duration-300"
            >
              {item.icon}
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h3>
              <p className="text-gray-600 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default About;
